import { GetObjectCommand, PutObjectCommand, ListObjectsV2Command } from '@aws-sdk/client-s3';
import { s3Client } from '../config/aws';
import type { User } from '../types';

const BUCKET_NAME = 'xrmgpublic';
const USER_PREFIX = 'internal/barker/userdata/';

// Helper function to normalize phone numbers for comparison
function normalizePhoneNumber(phone: string): string {
  // Remove all non-digit characters
  return phone.replace(/\D/g, '');
}

export function generateUserFolder(fullName: string): string {
  const sanitizedName = fullName.toLowerCase().replace(/[^a-z0-9]/g, '');
  const uniqueKey = crypto.randomUUID().split('-')[0];
  return `${sanitizedName}-${uniqueKey}`;
}

export async function findUserByCredentials(email: string, phone: string): Promise<User | null> {
  try {
    const command = new ListObjectsV2Command({
      Bucket: BUCKET_NAME,
      Prefix: USER_PREFIX
    });

    const response = await s3Client.send(command);
    if (!response.Contents) return null;

    const normalizedSearchPhone = normalizePhoneNumber(phone);

    for (const object of response.Contents) {
      if (object.Key?.endsWith('/user.json')) {
        try {
          const getCommand = new GetObjectCommand({
            Bucket: BUCKET_NAME,
            Key: object.Key
          });

          const data = await s3Client.send(getCommand);
          if (!data.Body) continue;

          const userData = JSON.parse(await data.Body.transformToString()) as User;
          // Compare normalized phone numbers
          if (userData.email === email && normalizePhoneNumber(userData.phone) === normalizedSearchPhone) {
            return userData;
          }
        } catch (error) {
          console.error('Error reading user data:', error);
        }
      }
    }

    return null;
  } catch (error) {
    console.error('Error searching for user:', error);
    return null;
  }
}

export async function saveUserData(user: User, isNewUser: boolean = false): Promise<boolean> {
  try {
    // Only check for existing user if this is a new user registration
    if (isNewUser) {
      const existingUser = await findUserByCredentials(user.email, user.phone);
      if (existingUser) {
        console.error('User already exists');
        return false;
      }
    }

    const key = `${USER_PREFIX}${user.cloudFolder}/user.json`;
    const command = new PutObjectCommand({
      Bucket: BUCKET_NAME,
      Key: key,
      Body: JSON.stringify(user),
      ContentType: 'application/json'
    });

    await s3Client.send(command);
    return true;
  } catch (error) {
    console.error('Error saving user data:', error);
    return false;
  }
}

export async function loadUserData(cloudFolder: string): Promise<User | null> {
  if (!cloudFolder) {
    console.log('No cloud folder provided, skipping user data load');
    return null;
  }

  try {
    const key = `${USER_PREFIX}${cloudFolder}/user.json`;
    
    // First check if the file exists
    try {
      const listCommand = new ListObjectsV2Command({
        Bucket: BUCKET_NAME,
        Prefix: key,
        MaxKeys: 1
      });
      
      const listResponse = await s3Client.send(listCommand);
      if (!listResponse.Contents || listResponse.Contents.length === 0) {
        console.log(`User data file not found at ${key}`);
        return null;
      }
    } catch (error) {
      console.error('Error checking for user data file:', error);
      return null;
    }

    // If we get here, the file exists, so try to load it
    const command = new GetObjectCommand({
      Bucket: BUCKET_NAME,
      Key: key
    });

    const response = await s3Client.send(command);
    if (!response.Body) {
      console.log('No user data found in response body');
      return null;
    }

    const userData = await response.Body.transformToString();
    return JSON.parse(userData);
  } catch (error) {
    if ((error as any).name === 'NoSuchKey') {
      console.log(`No user data found at ${cloudFolder}`);
      return null;
    }
    console.error('Error loading user data:', error);
    return null;
  }
}